import { Modal, notification } from "antd";
import cuid from "cuid";
import { find, get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdOutlineBusiness,
  MdOutlineDelete,
  MdOutlineInfo,
  MdOutlineTableChart,
  MdUpload,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import {
  CREATE_ORGANIZATION,
  CREATE_ORGANIZATIONS,
  DELETE_ORGANIZATION,
  IMPORT_ORGANIZATIONS_FROM_FILE,
  UPDATE_ORGANIZATION,
} from "utils/api/graphql/mutations/organizations";
import { ORGANIZATIONS } from "utils/api/graphql/queries/organizations";
import {
  CONTACT_POINT_SYSTEM,
  CONTENT_TYPES,
  ORGANIZATION_TYPES,
  REGEX_EXPS,
} from "utils/constants";
import OrganizationCSVMappingModal from "../OrganizationCSVMappingModal";

export default function ContractSection({ organization = {} }) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const CONTRACT_SECTION = t("CONTRACT_SECTION", { returnObjects: true });
  const { data: branchesData, refetch: refetchBranches } = useQuery(
    ORGANIZATIONS,
    {
      variables: {
        where: {
          counterpartyContracts: {
            organization: {
              id: organization.id,
            },
          },
        },
      },
    },
  );

  const entities = useMemo(() => {
    const entitiesData = omitDeep(
      get(branchesData, "organizations.data") || [],
      "__typename",
    );
    return entitiesData.map((entity) => {
      const telecoms = get(entity, "telecoms", []);
      const zipCode = get(
        find(telecoms, { system: CONTACT_POINT_SYSTEM.ZIP_CODE }),
        "value",
        "",
      );
      const city = get(
        find(telecoms, { system: CONTACT_POINT_SYSTEM.CITY }),
        "value",
        "",
      );
      return {
        ...entity,
        zipCode,
        city,
      };
    });
  }, [branchesData]);

  const [createOrganization] = useMutation(CREATE_ORGANIZATION);
  const [createOrganizations] = useMutation(CREATE_ORGANIZATIONS);
  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION);
  const [deleteOrganization] = useMutation(DELETE_ORGANIZATION);
  const [importOrganizationsFromFile] = useMutation(
    IMPORT_ORGANIZATIONS_FROM_FILE,
  );

  const [showAddEntity, setShowAddEntity] = useState(false);
  const [newEntity, setNewEntity] = useState({
    name: "",
    zipCode: "",
    city: "",
    description: "",
  });
  const [savingEntity, setSavingEntity] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [showCSVMapping, setShowCSVMapping] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const fileInputRef = useRef(null);

  const isCollectivity = organization?.type === ORGANIZATION_TYPES.CDG;
  const entityLabel = isCollectivity
    ? CONTRACT_SECTION.ENTITY_LABEL.COLLECTIVITIES
    : CONTRACT_SECTION.ENTITY_LABEL.ENTITIES;
  const entityLabelSingular = isCollectivity
    ? CONTRACT_SECTION.ENTITY_LABEL.COLLECTIVITY_SINGULAR
    : CONTRACT_SECTION.ENTITY_LABEL.ENTITY_SINGULAR;

  async function handleAddEntity() {
    if (!newEntity.name.trim()) {
      notification.error({
        message: CONTRACT_SECTION.NOTIFICATIONS.ADD_ERROR,
        description:
          t("CONTRACT_SECTION.FORM.NAME_REQUIRED") || "Name is required",
      });
      return;
    }

    if (isCollectivity && (!newEntity.zipCode || !newEntity.zipCode.trim())) {
      notification.error({
        message: CONTRACT_SECTION.NOTIFICATIONS.ADD_ERROR,
        description:
          t("CONTRACT_SECTION.FORM.ZIPCODE_REQUIRED") || "Zip code is required",
      });
      return;
    }

    if (
      isCollectivity &&
      newEntity.zipCode &&
      !new RegExp(REGEX_EXPS.zipCode).test(newEntity.zipCode.trim())
    ) {
      notification.error({
        message: CONTRACT_SECTION.NOTIFICATIONS.ADD_ERROR,
        description:
          t("CONTRACT_SECTION.FORM.INVALID_ZIPCODE") ||
          "Invalid zip code format. Please enter a valid 5-digit French zip code.",
      });
      return;
    }

    // Check for duplicate organization
    const normalizedName = newEntity.name.trim().toLowerCase();
    const normalizedZipCode = newEntity.zipCode.trim();
    const isDuplicate = entities.some((entity) => {
      const entityName = (entity.name || "").trim().toLowerCase();
      const entityZipCode = (entity.zipCode || "").trim();
      return (
        entityName === normalizedName && entityZipCode === normalizedZipCode
      );
    });

    if (isDuplicate) {
      notification.warning({
        message: CONTRACT_SECTION.NOTIFICATIONS.ADD_ERROR,
        description:
          t("CONTRACT_SECTION.NOTIFICATIONS.DUPLICATE_ORGANIZATION") ||
          "An organization with the same name and zip code already exists.",
      });
      return;
    }

    setSavingEntity(true);

    try {
      const { errors } = await createOrganization({
        variables: {
          data: {
            id: cuid(),
            name: newEntity.name,
            type: isCollectivity
              ? ORGANIZATION_TYPES.MUN
              : ORGANIZATION_TYPES.OTHER,
            active: true,
            additionalInfo: {
              description: newEntity.description || "",
            },
            counterpartyContracts: {
              id: cuid(),
              organization: {
                id: organization.id,
              },
            },
            telecoms: [
              {
                id: cuid(),
                system: CONTACT_POINT_SYSTEM.ZIP_CODE,
                value: newEntity.zipCode,
              },
              {
                id: cuid(),
                system: CONTACT_POINT_SYSTEM.CITY,
                value: newEntity.city,
              },
            ],
          },
        },
      });

      if (!errors) {
        setNewEntity({
          name: "",
          code: "",
          description: "",
          zipCode: "",
          city: "",
        });
        setShowAddEntity(false);
        await refetchBranches();
        notification.success({
          message: CONTRACT_SECTION.NOTIFICATIONS.ADD_SUCCESS,
          description: t("CONTRACT_SECTION.NOTIFICATIONS.ADD_SUCCESS_DESC", {
            label:
              entityLabelSingular.charAt(0).toUpperCase() +
              entityLabelSingular.slice(1),
          }),
        });
      } else {
        notification.error({
          message: CONTRACT_SECTION.NOTIFICATIONS.ADD_ERROR,
          description: CONTRACT_SECTION.NOTIFICATIONS.ADD_ERROR_DESC,
        });
      }
    } catch (error) {
      console.error("Error adding entity:", error);
      notification.error({
        message: CONTRACT_SECTION.NOTIFICATIONS.ADD_ERROR,
        description: CONTRACT_SECTION.NOTIFICATIONS.ADD_ERROR_DESC,
      });
    } finally {
      setSavingEntity(false);
    }
  }

  async function handleDeleteEntity(entityId) {
    Modal.confirm({
      title: CONTRACT_SECTION.NOTIFICATIONS.DELETE_CONFIRM_TITLE,
      content: t("CONTRACT_SECTION.NOTIFICATIONS.DELETE_CONFIRM_MESSAGE", {
        label: entityLabelSingular,
      }),
      okText: CONTRACT_SECTION.NOTIFICATIONS.DELETE_CONFIRM_OK,
      cancelText: CONTRACT_SECTION.NOTIFICATIONS.DELETE_CONFIRM_CANCEL,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const { errors } = await deleteOrganization({
            variables: {
              where: {
                id: entityId,
              },
            },
          });

          if (!errors) {
            await refetchBranches();
            notification.success({
              message: CONTRACT_SECTION.NOTIFICATIONS.DELETE_SUCCESS,
              description: t(
                "CONTRACT_SECTION.NOTIFICATIONS.DELETE_SUCCESS_DESC",
                {
                  label:
                    entityLabelSingular.charAt(0).toUpperCase() +
                    entityLabelSingular.slice(1),
                },
              ),
            });
          } else {
            notification.error({
              message: CONTRACT_SECTION.NOTIFICATIONS.DELETE_ERROR,
              description: CONTRACT_SECTION.NOTIFICATIONS.DELETE_ERROR_DESC,
            });
          }
        } catch (error) {
          console.error("Error deleting entity:", error);
          notification.error({
            message: CONTRACT_SECTION.NOTIFICATIONS.DELETE_ERROR,
            description: CONTRACT_SECTION.NOTIFICATIONS.DELETE_ERROR_DESC,
          });
        }
      },
    });
  }

  async function toggleEntityActive(entityId, isActive) {
    try {
      const { errors } = await updateOrganization({
        variables: {
          where: {
            id: entityId,
          },
          data: {
            active: !isActive,
          },
        },
      });

      if (!errors) {
        await refetchBranches();
        notification.success({
          message: CONTRACT_SECTION.NOTIFICATIONS.UPDATE_SUCCESS,
          description: CONTRACT_SECTION.NOTIFICATIONS.UPDATE_SUCCESS_DESC,
        });
      } else {
        notification.error({
          message: CONTRACT_SECTION.NOTIFICATIONS.UPDATE_ERROR,
          description: CONTRACT_SECTION.NOTIFICATIONS.UPDATE_ERROR_DESC,
        });
      }
    } catch (error) {
      console.error("Error updating entity:", error);
      notification.error({
        message: CONTRACT_SECTION.NOTIFICATIONS.UPDATE_ERROR,
        description: CONTRACT_SECTION.NOTIFICATIONS.UPDATE_ERROR_DESC,
      });
    }
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      CONTENT_TYPES.CSV,
      CONTENT_TYPES.XLSX,
      CONTENT_TYPES.XLS,
      CONTENT_TYPES.SPREADSHEET,
    ];
    const fileType = file.type || "";
    const fileName = file.name.toLowerCase();
    const isValidType =
      validTypes.includes(fileType) ||
      fileName.endsWith(".csv") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls");

    if (!isValidType) {
      notification.error({
        message: t("CONTRACT_SECTION.INVALID_FILE_TYPE") || "Invalid file type",
        description:
          t("CONTRACT_SECTION.INVALID_FILE_TYPE_DESC") ||
          "Please select a CSV, XLS, or XLSX file",
      });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Check if it's an Excel file
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

    if (isExcel) {
      // Use backend import mutation for Excel files
      handleExcelImport(file);
    } else {
      // Use CSV mapping modal popup for CSV files (same as DeploymentManager)
      setCsvFile(file);
      setShowCSVMapping(true);
    }

    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function handleExcelImport(file) {
    setUploadingExcel(true);

    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const { data, errors } = await importOrganizationsFromFile({
        variables: {
          data: {
            base64,
            parentOrganization: {
              id: organization.id,
            },
          },
        },
      });

      if (!errors) {
        const count = data?.importOrganizationsFromFile?.length || 0;
        notification.success({
          message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_SUCCESS,
          description:
            t("CONTRACT_SECTION.NOTIFICATIONS.IMPORT_SUCCESS_DESC", {
              count,
            }) || `${count} organization(s) imported successfully`,
        });
        await refetchBranches();
      } else {
        notification.error({
          message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR,
          description: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR_DESC,
        });
      }
    } catch (error) {
      console.error("Error importing Excel file:", error);
      notification.error({
        message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR,
        description: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR_DESC,
      });
    } finally {
      setUploadingExcel(false);
    }
  }

  function handleFileButtonClick() {
    fileInputRef.current?.click();
  }

  async function handleCSVImport(organizationsToImport) {
    if (!organizationsToImport || organizationsToImport.length === 0) return;

    setUploadingExcel(true);
    setShowCSVMapping(false);

    // Validate all organizations have required fields and valid zipCode
    const zipCodeRegex = new RegExp(REGEX_EXPS.zipCode);
    const invalidOrgs = organizationsToImport.filter(
      (org) =>
        !org.name ||
        !org.name.trim() ||
        !org.zipCode ||
        !org.zipCode.trim() ||
        !zipCodeRegex.test(org.zipCode.trim()),
    );

    if (invalidOrgs.length > 0) {
      notification.error({
        message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR,
        description:
          t("CONTRACT_SECTION.IMPORT_INVALID_DATA") ||
          "Some organizations have invalid data. Name and valid zip code are required.",
      });
      setUploadingExcel(false);
      setCsvFile(null);
      return;
    }

    // Check for duplicates against existing organizations
    // Create a set of existing organization keys (name + zipCode, case-insensitive)
    const existingKeys = new Set(
      entities.map((entity) => {
        const name = (entity.name || "").trim().toLowerCase();
        const zipCode = (entity.zipCode || "").trim();
        return `${name}::${zipCode}`;
      }),
    );

    // Filter out duplicates
    const newOrganizations = [];
    const duplicateOrganizations = [];

    organizationsToImport.forEach((org) => {
      const name = (org.name || "").trim().toLowerCase();
      const zipCode = (org.zipCode || "").trim();
      const key = `${name}::${zipCode}`;

      if (existingKeys.has(key)) {
        duplicateOrganizations.push(org);
      } else {
        newOrganizations.push(org);
        // Add to existing keys to avoid duplicates within the same import batch
        existingKeys.add(key);
      }
    });

    if (newOrganizations.length === 0) {
      notification.warning({
        message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR,
        description:
          t("CONTRACT_SECTION.IMPORT_ALL_DUPLICATES", {
            count: duplicateOrganizations.length,
          }) ||
          `All ${duplicateOrganizations.length} organization(s) already exist.`,
      });
      setUploadingExcel(false);
      setCsvFile(null);
      return;
    }

    try {
      const { errors } = await createOrganizations({
        variables: {
          data: newOrganizations.map((org) => ({
            id: cuid(),
            name: org.name,
            type: isCollectivity
              ? ORGANIZATION_TYPES.MUN
              : ORGANIZATION_TYPES.OTHER,
            active: true,
            additionalInfo: {
              description: org.description || "",
            },
            telecoms: [
              ...(org.zipCode
                ? [
                    {
                      id: cuid(),
                      system: CONTACT_POINT_SYSTEM.ZIP_CODE,
                      value: org.zipCode,
                    },
                  ]
                : []),
              ...(org.city
                ? [
                    {
                      id: cuid(),
                      system: CONTACT_POINT_SYSTEM.CITY,
                      value: org.city,
                    },
                  ]
                : []),
            ],
            counterpartyContracts: {
              id: cuid(),
              organization: {
                id: organization.id,
              },
            },
          })),
        },
      });

      if (!errors) {
        const addedCount = newOrganizations.length;
        const skippedCount = duplicateOrganizations.length;

        if (skippedCount > 0) {
          notification.success({
            message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_SUCCESS,
            description:
              t("CONTRACT_SECTION.NOTIFICATIONS.IMPORT_SUCCESS_WITH_SKIPPED", {
                added: addedCount,
                skipped: skippedCount,
              }) ||
              `${addedCount} organization(s) added, ${skippedCount} duplicate(s) skipped.`,
          });
        } else {
          notification.success({
            message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_SUCCESS,
            description:
              t("CONTRACT_SECTION.NOTIFICATIONS.IMPORT_SUCCESS_DESC", {
                count: addedCount,
              }) || `${addedCount} organization(s) imported successfully.`,
          });
        }
        await refetchBranches();
      } else {
        notification.error({
          message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR,
          description: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR_DESC,
        });
      }
    } catch (error) {
      console.error("Error importing organizations:", error);
      notification.error({
        message: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_ERROR,
        description: CONTRACT_SECTION.NOTIFICATIONS.IMPORT_FILE_ERROR_DESC,
      });
    } finally {
      setUploadingExcel(false);
      setCsvFile(null);
    }
  }

  return (
    <div className="company__space-y-8">
      <div>
        <div className="company__flex company__items-center company__justify-between company__mb-4">
          <h3 className="company__text-lg company__font-semibold company__text-slate-900">
            {t("CONTRACT_SECTION.HEADER.TITLE", { label: entityLabel })}
          </h3>
          <div className="company__flex company__items-center company__gap-2">
            {!showAddEntity && (
              <Button
                onClick={() => setShowAddEntity(true)}
                type="primary--link"
              >
                <MdAdd size={16} />
                {t("CONTRACT_SECTION.ADD_BUTTON", {
                  label: entityLabelSingular,
                })}
              </Button>
            )}
            {isCollectivity && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={[
                    ".csv",
                    ".xlsx",
                    ".xls",
                    CONTENT_TYPES.CSV,
                    CONTENT_TYPES.XLS,
                    CONTENT_TYPES.XLSX,
                    CONTENT_TYPES.SPREADSHEET,
                  ].join(",")}
                  onChange={handleFileChange}
                  disabled={uploadingExcel}
                  style={{ display: "none" }}
                />
                <Button
                  type="success"
                  onClick={handleFileButtonClick}
                  disabled={uploadingExcel}
                >
                  <MdUpload size={16} />
                  {uploadingExcel
                    ? CONTRACT_SECTION.HEADER.IMPORT_BUTTON_LOADING
                    : CONTRACT_SECTION.HEADER.IMPORT_BUTTON}
                </Button>
              </>
            )}
          </div>
        </div>

        {isCollectivity && (
          <div className="company__mb-4 company__p-4 company__bg-blue-50 company__border company__border-blue-200 company__rounded-lg">
            <div className="company__flex company__gap-3">
              <MdOutlineTableChart
                className="company__w-5 company__h-5 company__text-blue-600 company__flex-shrink-0"
                style={{ marginTop: "0.125rem" }}
              />
              <div className="company__text-sm company__text-blue-900">
                <p className="company__font-medium company__mb-2">
                  {CONTRACT_SECTION.FILE_FORMAT.TITLE}
                </p>
                <ul className="company__list-disc company__list-inside company__space-y-1 company__text-blue-800">
                  <li>{CONTRACT_SECTION.FILE_FORMAT.HEADER_LINE}</li>
                  <li>{CONTRACT_SECTION.FILE_FORMAT.DATA_LINES}</li>
                  <li>{CONTRACT_SECTION.FILE_FORMAT.SEPARATORS}</li>
                </ul>
                <p className="company__mt-2" style={{ fontStyle: "italic" }}>
                  {CONTRACT_SECTION.FILE_FORMAT.EXAMPLE}{" "}
                  <code className="company__bg-blue-100 company__px-2 company__py-1 company__rounded">
                    {CONTRACT_SECTION.FILE_FORMAT.EXAMPLE_VALUE}
                  </code>
                </p>
              </div>
            </div>
          </div>
        )}

        {showAddEntity && (
          <div className="company__p-12 company__bg-blue-50 company__border company__border-blue-200 company__rounded-lg company__space-y-3">
            <Input
              value={newEntity.name}
              onChange={(e) =>
                setNewEntity({ ...newEntity, name: e.target.value })
              }
              placeholder={
                isCollectivity
                  ? CONTRACT_SECTION.FORM.NAME_PLACEHOLDER_COLLECTIVITY
                  : CONTRACT_SECTION.FORM.NAME_PLACEHOLDER_ENTITY
              }
              label={t("CONTRACT_SECTION.FORM.NAME_LABEL", {
                label: entityLabelSingular,
              })}
              required
              style={{ backgroundColor: COLORS.C_WHITE }}
            />

            {!isCollectivity && (
              <Input
                value={newEntity.code}
                onChange={(e) =>
                  setNewEntity({ ...newEntity, code: e.target.value })
                }
                placeholder={CONTRACT_SECTION.FORM.CODE_PLACEHOLDER}
                label={CONTRACT_SECTION.FORM.CODE_LABEL}
                style={{ backgroundColor: COLORS.C_WHITE }}
              />
            )}

            {isCollectivity && (
              <div className="company__grid company__grid-cols-2 company__gap-3">
                <Input
                  value={newEntity.zipCode}
                  onChange={(e) =>
                    setNewEntity({
                      ...newEntity,
                      zipCode: e.target.value,
                    })
                  }
                  placeholder={CONTRACT_SECTION.FORM.POSTAL_CODE_PLACEHOLDER}
                  label={CONTRACT_SECTION.FORM.POSTAL_CODE_LABEL}
                  required
                  style={{ backgroundColor: COLORS.C_WHITE }}
                />
                <Input
                  value={newEntity.city}
                  onChange={(e) =>
                    setNewEntity({ ...newEntity, city: e.target.value })
                  }
                  placeholder={CONTRACT_SECTION.FORM.CITY_PLACEHOLDER}
                  label={CONTRACT_SECTION.FORM.CITY_LABEL}
                  style={{ backgroundColor: COLORS.C_WHITE }}
                />
              </div>
            )}

            <Input
              type="textarea"
              rows={2}
              value={newEntity.description}
              onChange={(e) =>
                setNewEntity({ ...newEntity, description: e.target.value })
              }
              placeholder={
                isCollectivity
                  ? CONTRACT_SECTION.FORM.DESCRIPTION_PLACEHOLDER_COLLECTIVITY
                  : CONTRACT_SECTION.FORM.DESCRIPTION_PLACEHOLDER_ENTITY
              }
              label={CONTRACT_SECTION.FORM.DESCRIPTION_LABEL}
              style={{ backgroundColor: COLORS.C_WHITE }}
            />

            <div className="company__flex company__gap-2">
              <Button
                type="secondary--link"
                onClick={() => {
                  setShowAddEntity(false);
                  setNewEntity({
                    name: null,
                    code: null,
                    description: null,
                    zipCode: null,
                    city: null,
                  });
                }}
              >
                {CONTRACT_SECTION.FORM.BUTTON_CANCEL}
              </Button>
              <Button
                type="primary"
                onClick={handleAddEntity}
                disabled={!newEntity.name.trim() || savingEntity}
              >
                {savingEntity
                  ? CONTRACT_SECTION.FORM.BUTTON_ADDING
                  : CONTRACT_SECTION.FORM.BUTTON_ADD}
              </Button>
            </div>
          </div>
        )}
        <p className="company__text-sm company__text-slate-600 company__mb-4">
          {isCollectivity
            ? CONTRACT_SECTION.DESCRIPTION.COLLECTIVITY
            : CONTRACT_SECTION.DESCRIPTION.ENTITY}
        </p>

        <div className="company__space-y-3">
          {entities.length === 0 ? (
            <div className="company__p-6 company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg company__text-center">
              <MdOutlineBusiness className="company__w-8 company__h-8 company__text-slate-400 company__mx-auto company__mb-2" />
              <p className="company__text-slate-600 company__text-sm">
                {t("CONTRACT_SECTION.EMPTY_STATE.NO_ENTITY", {
                  label: entityLabelSingular,
                })}
              </p>
              <p className="company__text-slate-500 company__text-xs company__mt-1">
                {isCollectivity
                  ? CONTRACT_SECTION.EMPTY_STATE.CTA_COLLECTIVITY
                  : CONTRACT_SECTION.EMPTY_STATE.CTA_ENTITY}
              </p>
            </div>
          ) : (
            <div className="company__space-y-2">
              {entities.map((entity) => (
                <div
                  key={entity.id}
                  className={`company__p-6 company__border company__rounded-lg company__flex company__items-start company__justify-between ${
                    entity.active
                      ? "company__bg-white company__border-slate-300"
                      : "company__bg-slate-50 company__border-slate-200 company__opacity-60"
                  }`}
                >
                  <div className="company__flex-1">
                    <div className="company__flex company__items-center company__gap-3">
                      <h4 className="company__font-medium company__text-slate-900">
                        {entity.name}
                      </h4>
                      {entity.siretNumber && (
                        <span className="company__px-2 company__py-1 company__bg-slate-100 company__text-slate-700 company__text-xs company__rounded">
                          {entity.siretNumber}
                        </span>
                      )}
                      {(entity.zipCode || entity.city) && (
                        <span className="company__px-2 company__py-1 company__bg-blue-100 company__text-blue-700 company__text-xs company__rounded">
                          {entity.zipCode} {entity.city}
                        </span>
                      )}
                      <span
                        className={`company__px-2 company__py-1 company__text-xs company__rounded ${
                          entity.active
                            ? "company__bg-green-100 company__text-green-700"
                            : "company__bg-slate-200 company__text-slate-600"
                        }`}
                      >
                        {entity.active
                          ? CONTRACT_SECTION.ENTITY_CARD.STATUS_ACTIVE
                          : CONTRACT_SECTION.ENTITY_CARD.STATUS_INACTIVE}
                      </span>
                    </div>
                    {entity.description && (
                      <p className="company__text-sm company__text-slate-600 company__mt-1">
                        {entity.description}
                      </p>
                    )}
                  </div>
                  <div className="company__flex company__items-center company__gap-2">
                    <Button
                      onClick={() =>
                        toggleEntityActive(entity.id, entity.active)
                      }
                      type="secondary--link"
                      size="small"
                    >
                      {entity.active
                        ? CONTRACT_SECTION.ENTITY_CARD.BUTTON_DEACTIVATE
                        : CONTRACT_SECTION.ENTITY_CARD.BUTTON_ACTIVATE}
                    </Button>
                    <Button
                      onClick={() => handleDeleteEntity(entity.id)}
                      type="danger--link"
                      title={CONTRACT_SECTION.ENTITY_CARD.BUTTON_DELETE_TITLE}
                      size="small"
                    >
                      <MdOutlineDelete className="company__w-4 company__h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {isCollectivity && entities.filter((e) => e.active).length > 0 && (
          <div className="company__mt-4 company__p-3 company__bg-green-50 company__border company__border-green-200 company__rounded-lg company__flex company__gap-3">
            <MdOutlineInfo
              className="company__w-5 company__h-5 company__text-green-600 company__flex-shrink-0"
              style={{ marginTop: "0.125rem" }}
            />
            <div className="company__text-sm company__text-green-900">
              <span className="company__font-medium">
                {CONTRACT_SECTION.INFO_BOX.TITLE}
              </span>{" "}
              {t("CONTRACT_SECTION.INFO_BOX.MESSAGE", {
                count: entities.filter((e) => e.active).length,
              })}
            </div>
          </div>
        )}
      </div>

      <OrganizationCSVMappingModal
        isOpen={showCSVMapping}
        onClose={() => {
          setShowCSVMapping(false);
          setCsvFile(null);
        }}
        onImport={handleCSVImport}
        csvFile={csvFile}
      />
    </div>
  );
}
